import { DollarSign, TrendingUp, ShoppingCart, Receipt, Tag, Gift, RotateCcw, TrendingDown } from 'lucide-react';

export function KPICards({ data, previousSummary }) {

  const defaultSummary = {
    TotalOrders_Landed: 0,
    Gross: 0,
    NetSale: 0,
    Tax: 0,
    Discount: 0,
    FocAmount: 0,
    SaleReturnAmount: 0,
    DeliveryCharges: 0,
  };

  const summary = {
    ...defaultSummary,
    ...(data?.summary || {})
  };
  const current = { ...defaultSummary, ...(data?.summary || {}) };
  const prev = {
    ...defaultSummary,
    ...(previousSummary || {})
  };

  const calcTrend = (cur, pre) => {
    if (!pre || pre === 0) return { trend: 'N/A', trendUp: true, neutral: true };
    const change = ((cur - pre) / pre) * 100;
    return {
      trend: `${change > 0 ? '+' : ''}${change.toFixed(1)}%`,
      trendUp: change >= 0,
      neutral: false,
    };
  };

  const trends = {
    totalOrders: calcTrend(current.TotalOrders_Landed, prev.TotalOrders_Landed),
    gross: calcTrend(current.Gross, prev.Gross),
    netSale: calcTrend(current.NetSale, prev.NetSale),
    tax: calcTrend(current.Tax, prev.Tax),
    discount: calcTrend(current.Discount, prev.Discount),
    foc: calcTrend(current.FocAmount, prev.FocAmount),
    returns: calcTrend(current.SaleReturnAmount, prev.SaleReturnAmount),
    delivery: calcTrend(current.DeliveryCharges, prev.DeliveryCharges),
  };

  const rawSummary = data?.summary || {};

  const gross = rawSummary.Gross || rawSummary.gross || rawSummary.GROSS || 0;
  const netSale = rawSummary.NetSale || rawSummary.netSale || rawSummary.NETSALE || rawSummary.NetSales || 0;
  const totalOrders = rawSummary.TotalOrders_Landed || rawSummary.totalOrders || rawSummary.TOTALORDERS || rawSummary.TotalOrders || 0;
  const tax = rawSummary.Tax || rawSummary.tax || rawSummary.TAX || 0;
  const discount = rawSummary.Discount || rawSummary.discount || rawSummary.DISCOUNT || 0;
  const focAmount = rawSummary.FocAmount || rawSummary.focAmount || rawSummary.FOCAMOUNT || rawSummary.Foc || 0;
  const saleReturnAmount = rawSummary.SaleReturnAmount || rawSummary.saleReturnAmount || rawSummary.SALERETURNAMOUNT || rawSummary.Returns || 0;
  const deliveryCharges = rawSummary.DeliveryCharges || rawSummary.deliveryCharges || rawSummary.DELIVERYCHARGES || 0;

  const kpis = [
    {
      title: 'Total Orders', value: current.TotalOrders_Landed, icon: ShoppingCart,
      color: 'from-purple-500 to-purple-600', trend: trends.totalOrders, isCount: true,
    },
    {
      title: 'Gross Sales', value: current.Gross, icon: DollarSign,
      color: 'from-blue-500 to-blue-600', trend: trends.gross,
    },
    {
      title: 'Total Tax', value: current.Tax, icon: Receipt,
      color: 'from-orange-500 to-orange-600', trend: trends.tax,
    },
    {
      title: 'Discounts', value: current.Discount, icon: Tag,
      color: 'from-pink-500 to-pink-600', trend: trends.discount,
    },
    {
      title: 'FOC Amount', value: current.FocAmount, icon: Gift,
      color: 'from-indigo-500 to-indigo-600', trend: trends.foc,
    },
    {
      title: 'Returns', value: current.SaleReturnAmount, icon: RotateCcw,
      color: 'from-red-500 to-red-600', trend: trends.returns,
    },
    {
      title: 'Delivery Charges', value: current.DeliveryCharges, icon: TrendingDown,
      color: 'from-cyan-500 to-cyan-600', trend: trends.delivery,
    },
    {
      title: 'Net Sales', value: current.NetSale, icon: TrendingUp,
      color: 'from-[#01796d] to-teal-600', trend: trends.netSale,
    },
  ];

  const formatValue = (value, isCount) => {
    // Ensure value is a number
    const numValue = Number(value) || 0;

    if (isCount) {
      return numValue.toLocaleString();
    }
    return `Rs ${numValue.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {kpis.map((kpi, index) => {
        const Icon = kpi.icon;
        return (
          <div
            key={index}
            className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 relative overflow-hidden group"
          >
            <div
              className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-300"
              style={{
                backgroundImage: `linear-gradient(to bottom right, ${kpi.color.includes('from-') ?
                  kpi.color.replace('from-', '') :
                  '#01796d'
                  })`
              }}
            ></div>

            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`bg-gradient-to-br ${kpi.color} p-3 rounded-xl shadow-lg`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <p className="text-lg text-slate-500 font-medium">{kpi.title}</p>
              </div>
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${kpi.trend.neutral ? 'bg-slate-100 text-slate-500' :
                kpi.trend.trendUp ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                {kpi.trend.trend}
                {!kpi.trend.neutral && (kpi.trend.trendUp ? '↑' : '↓')}
              </div>
            </div>

            <div className="space-y-1">
              <p className="text-2xl font-bold text-slate-900 p-0 m-0">
                {formatValue(kpi.value, kpi.isCount)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}